/**
 * Keeps the promo bar ([data-alert-banner]) and sub-banner ([data-sub-banner]) heights in sync after the
 * first paint. Used by ConditionalMain, which measures once synchronously before paint and then calls this.
 *
 * ⚠ The MutationObserver here sees EVERY DOM change on the page. It used to call getBoundingClientRect (a
 * forced synchronous layout) and rebuild a ResizeObserver on each one: 190-783ms of phone CPU on page load,
 * measured 2026-10-31, and more on every live update after (register mutation-observer-forced-layout-1031).
 * So it only notices WHICH element is mounted; heights come from ResizeObserver, which reports an initial
 * size as soon as it starts observing. bannerHeightWatcher.test.ts counts the layout reads.
 */
export function watchBannerHeights(
  setBannerHeight: (h: number) => void,
  setSubBannerHeight: (h: number) => void,
): () => void {
  const sizeOf = (entries: ResizeObserverEntry[]) =>
    (entries[0]?.borderBoxSize?.[0]?.blockSize) ?? (entries[0]?.contentRect.height ?? 0)
  const bannerRo = new ResizeObserver((entries) => setBannerHeight(sizeOf(entries)))
  const subRo = new ResizeObserver((entries) => setSubBannerHeight(sizeOf(entries)))
  let banner: Element | null = null
  let sub: Element | null = null

  const track = () => {
    const b = document.querySelector('[data-alert-banner]')
    if (b !== banner) {
      if (banner) bannerRo.unobserve(banner)
      banner = b
      if (b) bannerRo.observe(b)
      else setBannerHeight(0)
    }
    const s = document.querySelector('[data-sub-banner]')
    if (s !== sub) {
      if (sub) subRo.unobserve(sub)
      sub = s
      if (s) subRo.observe(s)
      else setSubBannerHeight(0)
    }
  }
  track()

  // Banner mount/unmount (dismiss or route change). A batch that adds or removes no ELEMENT (a price or
  // text update) cannot mount or unmount a banner, so it is skipped without touching the DOM.
  const hasElement = (nodes: NodeList) => Array.prototype.some.call(nodes, (n: Node) => n.nodeType === 1)
  const mo = new MutationObserver((records) => {
    if (records.some((r) => hasElement(r.addedNodes) || hasElement(r.removedNodes))) track()
  })
  mo.observe(document.body, { childList: true, subtree: true })

  return () => {
    mo.disconnect()
    bannerRo.disconnect()
    subRo.disconnect()
  }
}
